import CloseIcon from "@assets/svg/CloseIcon";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@components/ui/dialog";
import { Label } from "@components/ui/label";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { isNumber } from "lodash";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

interface WithdrawBalanceModalProps {
  openModal: boolean;
  balance: number;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  onSubmit: (value: number) => void;
}

const WithdrawBalanceModal = ({
  balance,
  openModal,
  setOpenModal,
  onSubmit,
}: WithdrawBalanceModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedOption, setSelectedOption] = useState<"all" | "others">("all");
  const [balanceInput, setBalanceInput] = useState<string | undefined>();

  const onClose = () => {
    setOpenModal(false);
    setSelectedOption("all");
    setBalanceInput(undefined);
  };

  const handleWithdrawNow = () => {
    if (selectedOption === "all") {
      onSubmit(balance);
      onClose();
    } else if (
      selectedOption === "others" &&
      balanceInput &&
      isNumber(Number(balanceInput)) &&
      Number(balanceInput) > 1 &&
      Number(balanceInput) <= balance
    ) {
      onSubmit(Number(balanceInput));
      onClose();
    } else {
      enqueueSnackbar("Amount must be no less than £1.", { variant: "error" });
    }
  };

  useEffect(() => {
    if (selectedOption === "others") {
      setBalanceInput(balance?.toString());
    }
  }, [selectedOption, balance]);

  return (
    <Dialog
      open={openModal}
      onOpenChange={(value) => {
        setOpenModal(value);
        if (!value) {
          onClose();
        }
      }}
    >
      <DialogContent showCloseButton={false} className="bg-white">
        <div className="flex justify-between">
          <div className="flex flex-col">
            <span className="font-semibold text-lg">Withdraw Balance</span>
            <span className="font-medium text-xs text-light-black">
              Withdraw your available balance securely and instantly.
            </span>
          </div>
          <DialogClose asChild>
            <button
              onClick={() => setOpenModal(false)}
              className="h-max p-2 rounded-md hover:bg-gray-50"
            >
              <CloseIcon className={"w-5 h-5"} />
            </button>
          </DialogClose>
        </div>

        <div className="flex flex-col gap-4">
          <RadioGroup
            defaultValue={selectedOption}
            onValueChange={(value) =>
              setSelectedOption(value as "all" | "others")
            }
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="all" id="r1" />
              <Label htmlFor="r1">£ {balance}</Label>
            </div>
            <div className="flex items-center gap-3">
              <RadioGroupItem value="others" id="r2" />
              <Label htmlFor="r2">Other amount</Label>
            </div>
          </RadioGroup>
          {selectedOption === "others" ? (
            <input
              className="font-medium text-xs text-primary border rounded-md h-10 w-48 px-3"
              placeholder="Enter withdraw amount"
              value={balanceInput}
              type="number"
              onChange={(e) => setBalanceInput(e?.target?.value)}
            />
          ) : null}
        </div>

        <DialogFooter>
          <button
            onClick={handleWithdrawNow}
            className="font-semibold text-sm bg-primary hover:bg-black text-white py-2.5 px-5 rounded-md flex items-center justify-self-end"
          >
            Withdraw Now
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WithdrawBalanceModal;
